import React from 'react';
import { getSpreadDimensions } from '../../../BookDesign/constants';
import ElementRenderer from './ElementRenderer';
import { getPageNumber } from '../../../../utils/pageMapping';

export default function CoverSpreadPreview({ spread, book, scale = 1, debugMode = false, targetHeight = 500, canvasMode = 'lulu', paperThicknessIn = 0.0025, bleedIn = 0.125, showBleed = false }) {
  const spreadNumber = parseInt(spread?.id?.replace('spread-', '') || 0, 10);
  const isCoverExterior = spreadNumber === 0;

  // Choose canvas: for exterior cover allow switching between design and Lulu canvas
  let dims;
  if (isCoverExterior) {
    const useLulu = canvasMode === 'lulu';
    dims = getSpreadDimensions(useLulu ? 'landscape-custom' : (book?.pageSize || 'us-trade'));
  } else {
    dims = getSpreadDimensions(book?.pageSize || 'us-trade');
  }
  const totalPages = parseInt(book?.totalPages || 0, 10);

  // Page numbers for labels
  const leftPageNumber = getPageNumber(spreadNumber, false, totalPages);
  const rightPageNumber = getPageNumber(spreadNumber, true, totalPages);

  // Cover colors
  const frontCoverColor = book?.coverDetails?.frontCover?.color || book?.frontCoverColor || '#FFFFFF';
  const backCoverColor = book?.coverDetails?.backCover?.color || book?.backCoverColor || '#FFFFFF';
  // For exterior cover preview, default the back (left) half to the same color as the front
  // to match the current product behavior (single "Cover Paper" selection) and PDF output.
  const effectiveBackCoverColor = isCoverExterior ? frontCoverColor : backCoverColor;

  // Simple visual spine width (approximate)
  const useLulu = isCoverExterior && canvasMode === 'lulu';
  // Lulu spine width derived from page count and paper thickness (inches per page)
  const computedSpinePx = Math.max(1, (totalPages || 0) * paperThicknessIn * 72);
  const spineWidth = useLulu ? computedSpinePx : 2 / (targetHeight / dims.height);

  // Compute scale to fit target height (fills the 500px wrapper neatly)
  const effectiveScale = targetHeight / dims.height;
  // Base width: if canvas is design, width is two pages; if Lulu, dims.width is full cover
  const baseWidth = isCoverExterior && useLulu ? dims.width : dims.width * 2;
  const containerStyle = {
    width: baseWidth * effectiveScale,
    height: dims.height * effectiveScale,
    margin: 0,
    padding: 0,
    backgroundColor: '#FFFFFF',
  };

  return (
    <div className="relative" style={containerStyle}>
      {isCoverExterior && (
        <>
          {(() => {
            const rawSpine = (useLulu ? spineWidth * effectiveScale : 2);
            const spineW = Math.max(1, Math.round(rawSpine));
            return (
              <>
                {/* Back cover (left half) */}
                <div className="absolute inset-y-0" style={{ left: 0, width: 'calc(50% + 1px)', backgroundColor: effectiveBackCoverColor }} />
                {/* Front cover (right half) */}
                <div className="absolute inset-y-0" style={{ right: 0, width: 'calc(50% + 1px)', backgroundColor: frontCoverColor }} />
                {/* Spine overlay centered at 50% with integer width to prevent subpixel gaps */}
                <div className="absolute inset-y-0" style={{ left: '50%', width: spineW, transform: `translateX(-${Math.floor(spineW/2)}px)`, backgroundColor: 'rgba(0,0,0,0.08)', zIndex: 1 }} />
              </>
            );
          })()}
        </>
      )}

      {/* Render elements (support both combined and per-page arrays) */}
      {(() => {
        const baseElements = (spread?.elements && spread.elements.length > 0)
          ? spread.elements
          : [
              ...(spread?.leftPageElements || []),
              ...(spread?.rightPageElements || [])
            ];

        if (isCoverExterior && canvasMode === 'lulu') {
          // Map design coordinates to Lulu cover canvas
          const designDims = getSpreadDimensions(book?.pageSize || 'us-trade');
          const designPageW = designDims.width;
          const designH = designDims.height;

          const luluW = dims.width; // full cover width
          const luluH = dims.height;
          const spinePx = Math.max(6, Math.floor(luluW * 0.02));
          const luluPageW = (luluW - spinePx) / 2;

          const scaleXLeftRight = luluPageW / designPageW;
          const scaleY = luluH / designH;
          const bleedPx = showBleed ? (bleedIn * 72) : 0; // Optional bleed for preview

          const transform = (el) => {
            const c = JSON.parse(JSON.stringify(el));
            const pos = c.position || {};
            const size = c.size || {};
            const x = typeof pos.x === 'number' ? pos.x : 0;
            const y = typeof pos.y === 'number' ? pos.y : 0;

            const onRight = x >= designPageW;
            const xLocal = onRight ? (x - designPageW) : x;

            const newX = (onRight ? (luluPageW + spinePx) : bleedPx) + (xLocal * scaleXLeftRight);
            const newY = bleedPx + y * scaleY;

            c.position = { ...pos, x: newX, y: newY };

            if (typeof size.width === 'number') c.size.width = size.width * scaleXLeftRight;
            if (typeof size.height === 'number') c.size.height = size.height * scaleY;

            return c;
          };

          const mapped = baseElements.map(transform);
          return mapped.map((element, idx) => (
            <ElementRenderer key={element.id || `el-${idx}`} element={element} scale={effectiveScale} />
          ));
        }

        return baseElements.map((element, idx) => (
          <ElementRenderer key={element.id || `el-${idx}`} element={element} scale={effectiveScale} />
        ));
      })()}

      {/* Center guide (hide for exterior cover, we show a spine instead) */}
      {!isCoverExterior && (
        <div className="absolute inset-y-0 left-1/2 border-l border-dashed border-gray-300" style={{ transform: 'translateX(-0.5px)' }} />
      )}

      {/* Page labels (hide for exterior cover) */}
      {!isCoverExterior && (
        <div className="absolute inset-0 flex items-center justify-between pointer-events-none px-4">
          <p className="text-sm text-gray-400">
            {leftPageNumber === 0 ? '' : `Page ${leftPageNumber}`}
          </p>
          <p className="text-sm text-gray-400">
            {rightPageNumber > totalPages ? '' : `Page ${rightPageNumber}`}
          </p>
        </div>
      )}
    </div>
  );
}
